<template>
  <RiskNoticeModal
    v-if="showRiskNotice && riskNoticeCopy"
    :title="riskNoticeCopy.title"
    :messages="riskNoticeCopy.messages"
    :confirm-text="riskNoticeCopy.confirm"
    @close="acknowledgeRiskNotice"
  />
  <div class="index-page">
    <section class="performance-highlight">
      <div class="highlight-header">
        <div>
          <h2>{{ performanceCopy.title }}</h2>
          <p>{{ performanceCopy.description }}</p>
        </div>
        <button class="secondary-button" type="button" @click="goToStrategyPerformance">
          {{ performanceCopy.cta }}
        </button>
      </div>

      <div class="highlight-body">
        <div v-if="highlightLoading" class="loading-state">
          <div class="spinner"></div>
          <span>{{ performanceCopy.loading }}</span>
        </div>
        <div v-else-if="highlightError" class="error-state">
          <p>{{ performanceCopy.errorPrefix }}{{ highlightError || performanceCopy.errorDefault }}</p>
          <button class="secondary-button" type="button" @click="fetchTopStrategies">
            {{ performanceCopy.retry }}
          </button>
        </div>
        <div v-else-if="topStrategies.length" class="strategy-preview-grid">
          <article
            v-for="strategy in topStrategies"
            :key="strategy.strategy_id"
            class="strategy-preview-card"
            @click="goToStrategyDetail(strategy.strategy_id)"
          >
            <header class="strategy-preview-header">
              <h3 class="strategy-title">
                <span>{{ getStrategyDisplayName(strategy) }}</span>
              </h3>
              <div class="strategy-meta">
                <span
                  v-if="isPreviewStrategy(strategy.strategy_name)"
                  class="preview-indicator"
                  :data-tooltip="performanceCopy.previewTooltip"
                >
                  {{ performanceCopy.previewLabel }}
                </span>
                <span v-if="strategy.rl_model_config?.region" class="region-chip">
                  {{ formatRegion(strategy.rl_model_config.region) }}
                </span>
              </div>
            </header>
            <p v-if="getDisplayStrategyType(strategy)" class="strategy-preview-type">
              {{ getDisplayStrategyType(strategy) }}
            </p>
            <dl class="strategy-preview-metrics">
              <div>
                <dt>{{ performanceCopy.metrics.winRate }}</dt>
                <dd :class="{ positive: getWinRate(strategy) >= 60, neutral: getWinRate(strategy) < 60 }">
                  {{ getWinRate(strategy).toFixed(1) }}%
                </dd>
              </div>
              <div>
                <dt>{{ performanceCopy.metrics.avgReturnPerTrade }}</dt>
                <dd :class="{ positive: getAvgReturn(strategy) >= 0, negative: getAvgReturn(strategy) < 0 }">
                  {{ getAvgReturn(strategy).toFixed(2) }}%
                </dd>
              </div>
              <div>
                <dt>{{ performanceCopy.metrics.totalReturn }}</dt>
                <dd :class="{ positive: getTotalProfit(strategy) >= 0, negative: getTotalProfit(strategy) < 0 }">
                  {{ (getTotalProfit(strategy) * 100).toFixed(2) }}%
                </dd>
              </div>
              <div>
                <dt>{{ performanceCopy.metrics.tradeCount }}</dt>
                <dd>{{ strategy.total_transactions }}</dd>
              </div>
            </dl>
            <footer class="strategy-preview-footer">
              <span v-if="strategy.earliest_transaction_date && strategy.latest_transaction_date">
                {{ strategy.earliest_transaction_date }} - {{ strategy.latest_transaction_date }}
              </span>
              <span class="cta">{{ performanceCopy.viewRecordCta }}</span>
            </footer>
          </article>
        </div>
        <div v-else class="empty-state">
          <p>{{ performanceCopy.empty }}</p>
        </div>
      </div>
    </section>

    <section class="capabilities">
      <div class="capabilities-header">
        <h2>{{ capabilitiesCopy.title }}</h2>
        <p>{{ capabilitiesCopy.description }}</p>
      </div>
      <div class="capability-grid">
        <article v-for="feature in capabilityCards" :key="feature.id" class="capability-card">
          <div class="capability-main">
            <div class="capability-icon">{{ feature.icon }}</div>
            <div class="capability-text">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </div>
          </div>
          <div class="capability-highlights">
            <span v-for="(highlight, idx) in feature.highlights" :key="idx" class="capability-pill">
              {{ highlight }}
            </span>
          </div>
          <button class="capability-cta" type="button" @click="goTo(feature.route)">
            {{ feature.cta }} →
          </button>
        </article>
      </div>
      <p class="confidence-note">
        {{ capabilitiesCopy.note }}
      </p>
    </section>

    <section class="hero-card">
      <div class="hero-content">
        <h1>{{ pageCopy.hero.title }}</h1>
        <p class="hero-subtitle">
          {{ pageCopy.hero.subtitle }}
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getStrategiesPerformance, getPublicStrategyHighlights } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { indexPageContent } from '@/content/indexPage';
import RiskNoticeModal from '@/components/common/RiskNoticeModal.vue';

const router = useRouter();
const authStore = useAuthStore();
const { locale } = useI18n();

const highlightLoading = ref(false);
const highlightError = ref(null);
const strategies = ref([]);
const isAuthenticated = computed(() => authStore.isAuthenticated);

const pageCopy = computed(
  () => indexPageContent[locale.value] ?? indexPageContent['en-US']
);
const performanceCopy = computed(() => pageCopy.value.performance);
const capabilitiesCopy = computed(() => pageCopy.value.capabilities);
const capabilityCards = computed(() => {
  const cards = capabilitiesCopy.value?.cards ?? [];
  return cards.filter((card) => card.id !== 'simulation');
});
const riskNoticeCopy = computed(() => pageCopy.value.riskNotice);

const showRiskNotice = ref(false);
const RISK_NOTICE_STORAGE_KEY = 'rquant-risk-notice-v1';

const fetchTopStrategies = async () => {
  highlightLoading.value = true;
  highlightError.value = null;
  try {
    if (isAuthenticated.value) {
      const response = await getStrategiesPerformance({ limit: 12, offset: 0 });
      strategies.value = response?.strategies || [];
    } else {
      const response = await getPublicStrategyHighlights({ limit: 12, top_n: 3 });
      const highlights = response?.strategies || [];
      strategies.value = highlights.map((item) => ({
        strategy_id: item.strategy_id,
        strategy_name: item.strategy_name,
        strategy_type: item.strategy_type,
        cn_name: item.cn_name,
        en_name: item.en_name,
        rl_model_config: item.market ? { region: item.market } : null,
        performance_metrics: {
          win_rate_percent: item.metrics?.win_rate_percent ?? 0,
          average_return_percent_per_trade: item.metrics?.average_return_percent_per_trade ?? 0,
          total_profit_sum: item.metrics?.total_profit_sum ?? 0
        },
        total_transactions: item.total_transactions ?? 0,
        earliest_transaction_date: item.earliest_transaction_date ?? null,
        latest_transaction_date: item.latest_transaction_date ?? null
      }));
    }
  } catch (error) {
    console.error('Failed to load strategy performance preview:', error);
    highlightError.value = error?.message || performanceCopy.value.errorDefault;
  } finally {
    highlightLoading.value = false;
  }
};

onMounted(() => {
  fetchTopStrategies();
  if (typeof window !== 'undefined') {
    try {
      const hasAcknowledged = window.localStorage.getItem(RISK_NOTICE_STORAGE_KEY) === 'ack';
      showRiskNotice.value = !hasAcknowledged;
    } catch (err) {
      console.warn('Unable to read risk notice acknowledgement:', err);
      showRiskNotice.value = true;
    }
  } else {
    showRiskNotice.value = true;
  }
});

watch(isAuthenticated, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    fetchTopStrategies();
  }
});

const topStrategies = computed(() => {
  const sorted = [...strategies.value].sort((a, b) => {
    const winRateA = a?.performance_metrics?.win_rate_percent ?? 0;
    const winRateB = b?.performance_metrics?.win_rate_percent ?? 0;
    return winRateB - winRateA;
  });
  return sorted.slice(0, 3);
});

const goToStrategyPerformance = () => {
  router.push('/strategy/performance');
};

const goToStrategyDetail = (strategyId) => {
  if (!strategyId) return;
  router.push(`/strategy/performance/${strategyId}`);
};

const goTo = (route) => {
  router.push(route);
};

const getWinRate = (strategy) => strategy?.performance_metrics?.win_rate_percent ?? 0;
const getAvgReturn = (strategy) => strategy?.performance_metrics?.average_return_percent_per_trade ?? 0;
const getTotalProfit = (strategy) => strategy?.performance_metrics?.total_profit_sum ?? 0;

const PREVIEW_SUFFIX = 'preview';

const stripPreviewSuffix = (name = '') => {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.toLowerCase().endsWith(PREVIEW_SUFFIX)
    ? trimmed.slice(0, trimmed.length - PREVIEW_SUFFIX.length).trim()
    : trimmed;
};

const isPreviewStrategy = (name = '') => {
  if (!name) return false;
  return name.trim().toLowerCase().endsWith(PREVIEW_SUFFIX);
};

const getBaseStrategyName = (name = '') => {
  return stripPreviewSuffix(name || '');
};

const getDisplayStrategyType = (strategy = {}) => {
  const type = (strategy?.strategy_type || '').trim();
  if (!type) return '';
  return type.toLowerCase() === 'rl_model' ? '' : type;
};

const getStrategyDisplayName = (strategy = {}) => {
  // Prefer locale-specific name explicitly returned from API, then fallback to config
  const cfg = strategy?.rl_model_config || {};
  const cnName = stripPreviewSuffix(strategy?.cn_name || cfg.cn_name || '');
  const enName = stripPreviewSuffix(strategy?.en_name || cfg.en_name || '');
  const currentLocale = locale.value;
  if (currentLocale === 'zh-CN' && cnName) return cnName;
  if (currentLocale === 'en-US' && enName) return enName;
  if (cnName && !enName) return cnName;
  if (enName) return enName;
  return getBaseStrategyName(strategy?.strategy_name);
};

const formatRegion = (region) => {
  if (!region) {
    return '';
  }
  return performanceCopy.value.regionLabel.replace('{region}', region);
};

const acknowledgeRiskNotice = () => {
  showRiskNotice.value = false;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(RISK_NOTICE_STORAGE_KEY, 'ack');
    } catch (err) {
      console.warn('Unable to persist risk notice acknowledgement:', err);
    }
  }
};
</script>

<style scoped>
.index-page {
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  padding-bottom: 4rem;
}

.hero-card {
  background: linear-gradient(140deg, rgba(59, 130, 246, 0.12) 0%, rgba(14, 165, 233, 0.08) 45%, rgba(14, 116, 144, 0.06) 100%);
  border-radius: var(--radius-xl);
  padding: 3rem;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--color-border);
  display: flex;
  justify-content: center;
}

.hero-content {
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
}

.hero-card h1 {
  margin: 0;
  font-size: 2.75rem;
  color: var(--color-text-primary);
  letter-spacing: -0.45px;
}

.hero-subtitle {
  margin: 0;
  font-size: 1.08rem;
  color: var(--color-text-secondary);
  line-height: 1.8;
  white-space: pre-line;
}

.secondary-button {
  padding: 0.75rem 1.4rem;
  border-radius: var(--radius-sm);
  background: rgba(59, 130, 246, 0.12);
  color: var(--color-primary-hover);
  box-shadow: 0 10px 24px rgba(59, 130, 246, 0.16);
  border: none;
  cursor: pointer;
  transition: background var(--transition-base), box-shadow var(--transition-base);
}

.secondary-button:hover {
  background: rgba(59, 130, 246, 0.18);
  box-shadow: 0 14px 30px rgba(59, 130, 246, 0.18);
}

.performance-highlight {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  padding: 2.6rem 2.5rem;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.highlight-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.highlight-header > div:first-child {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.highlight-header h2 {
  margin: 0 0 0.4rem;
  font-size: 1.9rem;
  color: var(--color-text-primary);
}

.highlight-header p {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.6;
  max-width: 620px;
  white-space: pre-line;
}

.highlight-body {
  min-height: 260px;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem 1rem;
  color: var(--color-text-secondary);
  text-align: center;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid rgba(148, 163, 184, 0.3);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.strategy-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.6rem;
}

.strategy-preview-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: var(--shadow-medium);
  padding: 1.6rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  cursor: pointer;
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.strategy-preview-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 44px rgba(15, 23, 42, 0.14);
}

.strategy-preview-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
}

.strategy-preview-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: var(--color-text-primary);
  text-align: center;
}

.strategy-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.preview-indicator {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #b45309;
  background-color: #fef3c7;
  text-transform: lowercase;
  position: relative;
  cursor: help;
  vertical-align: middle;
}

.preview-indicator::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: -0.4rem;
  transform: translate(-50%, 100%);
  background-color: #fff7ed;
  color: #92400e;
  padding: 0.35rem 0.6rem;
  border-radius: 0.4rem;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 5;
}

.preview-indicator::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -0.1rem;
  transform: translate(-50%, 100%);
  border-width: 0.35rem 0.35rem 0 0.35rem;
  border-style: solid;
  border-color: #fff7ed transparent transparent transparent;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 5;
}

.preview-indicator:hover::after,
.preview-indicator:hover::before {
  opacity: 1;
  transform: translate(-50%, calc(100% + 0.25rem));
}

.region-chip {
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: var(--color-primary-hover);
  font-size: 0.78rem;
  font-weight: 600;
}

.strategy-preview-type {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  text-align: center;
}

.strategy-preview-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.strategy-preview-metrics div {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.strategy-preview-metrics dt {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.strategy-preview-metrics dd {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.strategy-preview-metrics dd.positive {
  color: var(--color-success);
}

.strategy-preview-metrics dd.neutral {
  color: var(--color-text-primary);
}

.strategy-preview-metrics dd.negative {
  color: #ef4444;
}

.strategy-preview-footer {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.strategy-preview-footer .cta {
  color: var(--color-primary-hover);
  font-weight: 600;
  font-size: 0.88rem;
}

.capabilities {
  background: var(--color-surface-elevated);
  border-radius: var(--radius-xl);
  padding: 2.75rem 2.6rem;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
}

.capabilities-header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.9rem;
  color: var(--color-text-primary);
}

.capabilities-header p {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.7;
  max-width: 720px;
}

.capability-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.2rem;
}

.capability-card {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: var(--color-surface);
  box-shadow: var(--shadow-medium);
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.capability-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
}

.capability-main {
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
}

.capability-icon {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: rgba(59, 130, 246, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.7rem;
}

.capability-text h3 {
  margin: 0 0 0.35rem;
  font-size: 1.2rem;
  color: var(--color-text-primary);
}

.capability-text p {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.capability-highlights {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.capability-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.capability-cta {
  border: none;
  background: transparent;
  color: var(--color-primary-hover);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  align-self: flex-start;
}

.capability-cta:hover {
  color: var(--color-primary);
}

.confidence-note {
  margin: 0;
  padding: 1rem 1.2rem;
  border-radius: var(--radius-md);
  background: rgba(59, 130, 246, 0.08);
  color: var(--color-text-secondary);
  line-height: 1.6;
  border: 1px solid rgba(59, 130, 246, 0.18);
}

@media (max-width: 900px) {
  .hero-card {
    padding: 2.6rem;
  }

  .hero-card h1 {
    font-size: 2.35rem;
  }

  .hero-subtitle {
    font-size: 1.02rem;
  }

  .performance-highlight {
    padding: 2.3rem 2rem;
  }

  .capabilities {
    padding: 2.4rem 2rem;
  }
}

@media (max-width: 640px) {
  .index-page {
    gap: 2.4rem;
  }

  .hero-card {
    padding: 2.2rem 1.8rem;
  }

  .hero-card h1 {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 0.98rem;
  }

  .strategy-preview-metrics {
    grid-template-columns: 1fr;
  }

  .capability-grid {
    grid-template-columns: 1fr;
  }
}
</style>
